import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import Axios from 'axios';
import { httpService } from '../httpService';
import { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { HttpError } from '../error/httpError';

type ApiResponse = {
  status: 200;
  data: unknown;
};

export interface AxiosBaseQueryArgs<Meta, Response = ApiResponse> {
  meta?: Meta;
  prepareHeaders?: (
    headers: AxiosRequestHeaders,
    api: BaseQueryApi
  ) => AxiosRequestHeaders;
  transformResponse?: (response: Response) => unknown;
}

export interface ServiceExtraOptions {
  authRequired?: boolean;
}

const getRequestConfig = (args: string | AxiosRequestConfig) => {
  if (typeof args === 'string') {
    return { url: args };
  }

  return args;
};

const axiosBaseQuery = <
  Args extends AxiosRequestConfig | string = AxiosRequestConfig,
  Result = unknown,
  DefinitionExtraOptions = Record<string, unknown>,
  Meta = Record<string, unknown>
>({
  prepareHeaders,
  meta,
  transformResponse,
}: AxiosBaseQueryArgs<Meta> = {}): BaseQueryFn<
  Args,
  Result,
  unknown,
  DefinitionExtraOptions,
  Meta
> => {
  return async (args, api, extraOptions) => {
    try {
      const requestConfig = getRequestConfig(args);
      const result = await httpService.axios({
        ...requestConfig,
        headers: prepareHeaders
          ? prepareHeaders(requestConfig.headers || {}, api)
          : requestConfig.headers,
        signal: api.signal,
        ...extraOptions,
      });

      return {
        data: transformResponse ? transformResponse(result.data) : result.data,
      };
    } catch (err) {
      if (Axios.isAxiosError(err)) {
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data,
          },
          meta,
        };
      }

      const appError = err as HttpError;

      return {
        error: {
          status: appError.status,
          data: appError.message,
        },
        meta,
      };
    }
  };
};

export default axiosBaseQuery;
